import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { squeezoService } from '../services/squeezoService';
import type { SqueezoHistoryPoint } from '../types/squeezo';
import { useWebSocket } from '../contexts/WebSocketContext';
import { profileService } from '../services/profileService';
import { addNotification } from '../utils/notificationHistory';
import { SqueezoSparkline } from './SqueezoSparkline';
import { SqueezoChartModal } from './SqueezoChartModal';

interface SqueezoWidgetProps {
  onRemove: () => void;
}

interface SqueezoAudioSettings {
  soundEnabled: boolean;
  notificationsEnabled: boolean;
}

const defaultAudioSettings: SqueezoAudioSettings = {
  soundEnabled: false,
  notificationsEnabled: false,
};

// Play a beep sound using Web Audio API
const playBeep = async (audioContext: AudioContext | null) => {
  if (!audioContext) return;

  if (audioContext.state === 'suspended') {
    try {
      await audioContext.resume();
    } catch (e) {
      console.warn('Could not resume AudioContext:', e);
      return;
    }
  }

  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();

  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);

  oscillator.frequency.value = 660; // E5 note (different from Wiseguy's A5)
  oscillator.type = 'sine';

  gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);

  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + 0.2);
};

export const SqueezoWidget: React.FC<SqueezoWidgetProps> = ({ onRemove }) => {
  const [currentValue, setCurrentValue] = useState<number>(50);
  const [lastUpdate, setLastUpdate] = useState<string | null>(null);
  const [isChartOpen, setIsChartOpen] = useState(false);
  const { subscribe, isConnected } = useWebSocket();
  const queryClient = useQueryClient();

  // Audio settings state
  const [audioSettings, setAudioSettings] = useState<SqueezoAudioSettings>(defaultAudioSettings);
  const [isAudioPrimed, setIsAudioPrimed] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );
  const [isStateLoaded, setIsStateLoaded] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const isFirstUpdate = useRef(true);

  // Load state from backend
  useEffect(() => {
    const loadState = async () => {
      try {
        const response = await profileService.getFrontendState();
        const savedState = response.frontend_state?.squeezoWidget;
        if (savedState?.audioSettings) {
          setAudioSettings({
            ...defaultAudioSettings,
            ...savedState.audioSettings,
          });
        }
      } catch (error) {
        console.error('Failed to load squeezo widget state:', error);
      } finally {
        setIsStateLoaded(true);
      }
    };
    loadState();
  }, []);

  // Save state to backend (debounced)
  const saveStateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const saveState = useCallback(() => {
    if (!isStateLoaded) return;

    if (saveStateTimeoutRef.current) {
      clearTimeout(saveStateTimeoutRef.current);
    }

    saveStateTimeoutRef.current = setTimeout(async () => {
      try {
        await profileService.saveFrontendState({
          squeezoWidget: {
            audioSettings,
          },
        });
      } catch (error) {
        console.error('Failed to save squeezo widget state:', error);
      }
    }, 1000);
  }, [audioSettings, isStateLoaded]);

  useEffect(() => {
    saveState();
  }, [audioSettings, saveState]);

  // Prime audio system
  const primeAudio = async () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    if (audioContextRef.current.state === 'suspended') {
      await audioContextRef.current.resume();
    }
    setIsAudioPrimed(true);
    playBeep(audioContextRef.current);
  };

  // Request notification permission
  const requestNotificationPermission = async () => {
    if (typeof Notification === 'undefined') return false;
    if (Notification.permission === 'granted') {
      setNotificationPermission('granted');
      return true;
    }
    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      return permission === 'granted';
    }
    return false;
  };

  // Show notification
  const showNotification = (value: number) => {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;

    const emoji = value >= 66 ? '🟢' : value <= 33 ? '🔴' : '🟡';
    const bias = value >= 66 ? 'Bullish' : value <= 33 ? 'Bearish' : 'Neutral';
    const title = `${emoji} Squeezo: ${Math.round(value)}`;
    const body = `Squeeze-O-Meter updated to ${Math.round(value)} (${bias})`;

    // Store in notification history
    addNotification({ title, body, source: 'squeezo' });

    const notification = new Notification(title, {
      body,
      icon: '/favicon.ico',
      tag: 'squeezo-update',
      requireInteraction: false,
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
    };

    setTimeout(() => notification.close(), 5000);
  };

  // Toggle handlers
  const handleToggleSound = async () => {
    if (!audioSettings.soundEnabled) {
      await primeAudio();
    }
    setAudioSettings((prev) => ({ ...prev, soundEnabled: !prev.soundEnabled }));
  };

  const handleToggleNotifications = async () => {
    if (!audioSettings.notificationsEnabled) {
      const granted = await requestNotificationPermission();
      if (granted) {
        setAudioSettings((prev) => ({ ...prev, notificationsEnabled: true }));
        if (typeof Notification !== 'undefined') {
          new Notification('Squeezo Alerts', {
            body: 'You\'ll receive notifications when Squeezo updates.',
            icon: '/favicon.ico',
          });
        }
      }
    } else {
      setAudioSettings((prev) => ({ ...prev, notificationsEnabled: false }));
    }
  };

  // Fetch initial data (WebSocket handles real-time updates)
  const { data, isLoading, error } = useQuery({
    queryKey: ['squeezo', 'latest'],
    queryFn: () => squeezoService.getLatest(),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  // Fetch sparkline history
  const { data: historyData } = useQuery({
    queryKey: ['squeezo', 'history'],
    queryFn: () => squeezoService.getHistory(20),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  // Update current value when data changes
  useEffect(() => {
    if (data?.data?.value !== undefined) {
      setCurrentValue(data.data.value);
      setLastUpdate(data.data.created_at);
    }
  }, [data]);

  // WebSocket subscription for real-time updates
  useEffect(() => {
    return subscribe('jdash.squeezo', (payload: unknown) => {
      const msg = payload as { data?: { value?: number; created_at?: string } };
      if (msg.data?.value !== undefined) {
        const newValue = msg.data.value;
        setCurrentValue(newValue);
        setLastUpdate(msg.data.created_at ?? null);

        // Prepend to sparkline history cache
        const newPoint: SqueezoHistoryPoint = {
          value: newValue,
          created_at: msg.data.created_at ?? new Date().toISOString(),
        };
        queryClient.setQueryData(
          ['squeezo', 'history'],
          (old: { data: SqueezoHistoryPoint[] } | undefined) => {
            if (!old) return old;
            return { ...old, data: [newPoint, ...old.data.slice(0, 19)] };
          },
        );

        // Skip audio/notifications on first update (initial load)
        if (isFirstUpdate.current) {
          isFirstUpdate.current = false;
          return;
        }

        // Play audio if enabled and primed
        if (audioSettings.soundEnabled && isAudioPrimed && audioContextRef.current) {
          playBeep(audioContextRef.current);
        }

        // Show notification if enabled and tab is in background
        if (audioSettings.notificationsEnabled && document.hidden) {
          showNotification(newValue);
        }
      }
    });
  }, [subscribe, audioSettings, isAudioPrimed, queryClient]);

  // Calculate semicircular gauge (opening at bottom)
  const radius = 55;
  const strokeWidth = 8;
  const normalizedRadius = radius - strokeWidth / 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const arcLength = (circumference * 180) / 360;

  // Calculate needle position (semicircle from left to right)
  const angle = 180 + (currentValue / 100) * 180;
  const angleRad = (angle * Math.PI) / 180;
  const needleLength = normalizedRadius - 5;
  const needleX = 70 + needleLength * Math.cos(angleRad);
  const needleY = 70 + needleLength * Math.sin(angleRad);

  // Check if audio needs activation
  const needsAudioActivation = (audioSettings.soundEnabled || audioSettings.notificationsEnabled) && !isAudioPrimed;

  if (isLoading) {
    return (
      <div className="h-full flex flex-col bg-dark-surface rounded-lg overflow-hidden">
        <div className="widget-drag-handle flex items-center px-3 py-1.5 bg-chrome border-b border-dark-border cursor-move relative z-20">
          <h3 className="text-sm font-bold text-white uppercase tracking-[1.5px]">Squeezo</h3>
        </div>
        <div className="flex items-center justify-end px-2 py-1 bg-dark-bg/50 border-b border-dark-border relative z-20">
          <button
            onClick={onRemove}
            className="text-gray-400 hover:text-white hover:bg-bear-red/20 text-sm px-2 py-1 rounded transition-colors"
            title="Close widget"
          >
            ×
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-gray-400">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex flex-col bg-dark-surface rounded-lg overflow-hidden">
        <div className="widget-drag-handle flex items-center px-3 py-1.5 bg-chrome border-b border-dark-border cursor-move relative z-20">
          <h3 className="text-sm font-bold text-white uppercase tracking-[1.5px]">Squeezo</h3>
        </div>
        <div className="flex items-center justify-end px-2 py-1 bg-dark-bg/50 border-b border-dark-border relative z-20">
          <button
            onClick={onRemove}
            className="text-gray-400 hover:text-white hover:bg-bear-red/20 text-sm px-2 py-1 rounded transition-colors"
            title="Close widget"
          >
            ×
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center p-4 text-center">
          <div>
            <p className="text-gray-400 text-sm mb-1">Temporarily Unavailable</p>
            <p className="text-gray-500 text-xs">Please check back later</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-dark-surface rounded-lg overflow-hidden">
      {/* Title Bar - drag handle only */}
      <div className="widget-drag-handle flex items-center px-3 py-1.5 bg-chrome border-b border-dark-border cursor-move relative z-20">
        <h3 className="text-sm font-bold text-white uppercase tracking-[1.5px]">Squeezo</h3>
        {isConnected && (
          <span className="w-2 h-2 rounded-full bg-bull-blue animate-pulse ml-2" title="Live" />
        )}
      </div>

      {/* Toolbar with audio controls and close button */}
      <div className="flex items-center justify-between px-2 py-1 bg-dark-bg/50 border-b border-dark-border relative z-20">
        <div className="flex items-center gap-1">
          {/* Sound toggle */}
          <button
            onClick={handleToggleSound}
            className={`p-1 rounded transition-colors relative ${
              audioSettings.soundEnabled
                ? isAudioPrimed
                  ? 'bg-bull-blue/20 text-bull-blue'
                  : 'bg-accent-yellow/20 text-accent-yellow'
                : 'text-gray-400 hover:text-white hover:bg-dark-surface'
            }`}
            title={audioSettings.soundEnabled ? 'Mute' : 'Enable sound'}
          >
            {audioSettings.soundEnabled ? (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
              </svg>
            )}
            {audioSettings.soundEnabled && !isAudioPrimed && (
              <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-accent-yellow rounded-full animate-pulse" />
            )}
          </button>

          {/* Notification toggle */}
          <button
            onClick={handleToggleNotifications}
            className={`p-1 rounded transition-colors relative ${
              audioSettings.notificationsEnabled
                ? notificationPermission === 'granted'
                  ? 'bg-bull-blue/20 text-bull-blue'
                  : 'bg-accent-yellow/20 text-accent-yellow'
                : 'text-gray-400 hover:text-white hover:bg-dark-surface'
            }`}
            title={audioSettings.notificationsEnabled ? 'Disable notifications' : 'Enable notifications'}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            {notificationPermission === 'denied' && (
              <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-bear-red rounded-full" />
            )}
            {audioSettings.notificationsEnabled && !isAudioPrimed && (
              <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-accent-yellow rounded-full animate-pulse" />
            )}
          </button>
        </div>

        {/* Close button */}
        <button
          onClick={onRemove}
          className="text-gray-400 hover:text-white hover:bg-bear-red/20 text-sm px-2 py-1 rounded transition-colors"
          title="Close widget"
        >
          ×
        </button>
      </div>

      {/* Audio activation banner */}
      {needsAudioActivation && (
        <button
          onClick={primeAudio}
          className="flex items-center justify-center gap-1 px-2 py-1 bg-accent-yellow/20 text-accent-yellow text-[9px] hover:bg-accent-yellow/30 transition-colors cursor-pointer border-b border-accent-yellow/30"
        >
          <svg className="w-3 h-3 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
          </svg>
          <span>Click to activate audio</span>
        </button>
      )}

      {/* Gauge content */}
      <div className="flex-1 min-h-0 flex flex-col items-center justify-center p-1">
        {/* Semicircular gauge — clickable to open chart */}
        <div
          className="relative flex-1 flex items-center justify-center w-full min-h-0 cursor-pointer"
          onClick={() => setIsChartOpen(true)}
          title="Click to view history"
        >
          <svg className="w-full h-full max-w-full max-h-full" viewBox="0 0 140 110" preserveAspectRatio="xMidYMid meet">
            <defs>
              <linearGradient id="gauge-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#D8483B" />
                <stop offset="50%" stopColor="#C2A14E" />
                <stop offset="100%" stopColor="#1C76A2" />
              </linearGradient>
            </defs>

            {/* Background arc */}
            <circle
              cx="70"
              cy="70"
              r={normalizedRadius}
              fill="none"
              stroke="#2B2B2B"
              strokeWidth={strokeWidth}
              strokeDasharray={`${arcLength} ${circumference}`}
              strokeLinecap="round"
              transform="rotate(180 70 70)"
            />

            {/* Gradient gauge arc */}
            <circle
              cx="70"
              cy="70"
              r={normalizedRadius}
              fill="none"
              stroke="url(#gauge-gradient)"
              strokeWidth={strokeWidth}
              strokeDasharray={`${arcLength} ${circumference}`}
              strokeLinecap="round"
              transform="rotate(180 70 70)"
            />

            {/* Needle */}
            <line
              x1="70"
              y1="70"
              x2={needleX}
              y2={needleY}
              stroke="white"
              strokeWidth="2.5"
              strokeLinecap="round"
              className="transition-all duration-500"
            />

            {/* Needle center dot */}
            <circle
              cx="70"
              cy="70"
              r="4"
              fill="white"
            />

            {/* Needle tip dot */}
            <circle
              cx={needleX}
              cy={needleY}
              r="3"
              fill="white"
              className="transition-all duration-500"
            />

            {/* Center value */}
            <text
              x="70"
              y="68"
              textAnchor="middle"
              dominantBaseline="middle"
              className="text-4xl font-bold fill-gray-100"
            >
              {Math.round(currentValue)}
            </text>
          </svg>
        </div>

        {/* Sparkline */}
        {historyData?.data && historyData.data.length >= 2 && (
          <div className="w-full px-2 flex-shrink-0">
            <SqueezoSparkline
              data={historyData.data}
              onClick={() => setIsChartOpen(true)}
            />
          </div>
        )}

        {/* Label and timestamp */}
        <div className="text-center flex-shrink-0">
          <div className="text-[9px] font-semibold text-gray-400 uppercase tracking-wide">
            Squeeze-O-Meter
          </div>
          {lastUpdate && (
            <div className="text-[7px] text-gray-600">
              {new Date(lastUpdate).toLocaleTimeString()}
            </div>
          )}
        </div>
      </div>

      {/* Chart modal */}
      {isChartOpen && (
        <SqueezoChartModal
          currentValue={currentValue}
          onClose={() => setIsChartOpen(false)}
        />
      )}
    </div>
  );
};
